/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ProviderFormFields.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Form fields component for provider form.
────────────────────────────────────────────────────────────────*/

import { AlertCircle, Bot, Mic, TestTube } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { Button } from '../../../components/ui/button';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import Select from '../../../components/ui/select';
import type { AiProviderForm } from '../hooks/useProviderData';
import { PROVIDER_OPTIONS, AZURE_KEY_TYPE_OPTIONS, GOOGLE_KEY_TYPE_OPTIONS } from '../hooks/useProviderData';

interface ProviderFormFieldsProps {
  formData: AiProviderForm;
  errors: Record<string, string>;
  isEditMode: boolean;
  testingConnection: boolean;
  refreshingCapabilities: boolean;
  refreshingVoices: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onInputChange: (field: keyof AiProviderForm, value: any) => void;
  inputClasses: (hasError?: boolean) => string;
  onTestConnection: () => void;
  onRefreshCapabilities: () => void;
  onRefreshVoices: () => void;
}

type TooltipLabelProps = {
  label: string;
  tooltip?: string;
  required?: boolean;
  htmlFor?: string;
};

const TooltipLabel = ({ label, tooltip, required, htmlFor }: TooltipLabelProps) => {
  if (!tooltip) {
    return (
      <label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
        {label}
        {required ? ' *' : ''}
      </label>
    );
  }

  return (
    <SimpleTooltip content={tooltip}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
        {label}
        {required ? ' *' : ''}
      </label>
    </SimpleTooltip>
  );
};

export function ProviderFormFields({
  formData,
  errors,
  isEditMode,
  testingConnection,
  refreshingCapabilities,
  refreshingVoices,
  onInputChange,
  inputClasses,
  onTestConnection,
  onRefreshCapabilities,
  onRefreshVoices
}: ProviderFormFieldsProps) {
  const { t } = useI18n();

  const errorMessages = Object.entries(errors).length > 0 ? (
    <div className="mt-4 space-y-1">
      {Object.entries(errors).map(([key, message]) => {
        const labelMap: Record<string, string> = {
          provider: t('aiProviders.fields.provider.label'),
          name: t('aiProviders.fields.name.label'),
          apiKey: t('aiProviders.fields.apiKey.label'),
          baseUrl: t('aiProviders.fields.baseUrl.label'),
          organization: t('aiProviders.fields.organization.label')
        };
        return (
          <p key={key} className="flex items-center gap-2 text-xs text-red-600">
            <AlertCircle className="h-3.5 w-3.5" />
            <span>
              {labelMap[key] ?? key}: {message}
            </span>
          </p>
        );
      })}
    </div>
  ) : null;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 items-center gap-x-4 gap-y-6">
        {/* Fila 1: Name y Organization */}
        <TooltipLabel
          htmlFor="provider-name"
          label={t('aiProviders.fields.name.label')}
          tooltip={t('aiProviders.fields.name.tooltip')}
          required
        />
        <input
          id="provider-name"
          type="text"
          value={formData.name}
          onChange={event => onInputChange('name', event.target.value)}
          placeholder={t('aiProviders.fields.name.placeholder')}
          className={inputClasses(Boolean(errors.name))}
        />

        <TooltipLabel
          htmlFor="provider-organization"
          label={t('aiProviders.fields.organization.label')}
          tooltip={t('aiProviders.fields.organization.tooltip')}
        />
        <input
          id="provider-organization"
          type="text"
          value={formData.organization}
          onChange={event => onInputChange('organization', event.target.value)}
          placeholder={t('aiProviders.fields.organization.placeholder')}
          className={inputClasses(Boolean(errors.organization))}
        />

        {/* Fila 2: Provider y API Key Type */}
        <TooltipLabel
          label={t('aiProviders.fields.provider.label')}
          tooltip={t('aiProviders.fields.provider.tooltip')}
          required
        />
        <Select
          value={formData.provider}
          onValueChange={value => onInputChange('provider', value)}
          options={[
            { value: '', label: t('aiProviders.fields.provider.placeholder') },
            ...PROVIDER_OPTIONS
          ]}
          disabled={isEditMode}
          className={errors.provider ? 'border-red-500' : undefined}
        />

        {['azure', 'google'].includes(formData.provider) ? (
          <>
            <TooltipLabel
              label={
                formData.provider === 'azure'
                  ? t('aiProviders.fields.azureKeyTypeLabel', 'Azure API Key Type')
                  : t('aiProviders.fields.googleKeyTypeLabel', 'Google API Key Type')
              }
              tooltip={
                formData.provider === 'azure'
                  ? t('aiProviders.fields.azureKeyTypeTooltip', 'Select the Azure product this key targets.')
                  : t('aiProviders.fields.googleKeyTypeTooltip', 'Select the Google product this key targets.')
              }
            />
            {formData.provider === 'azure' ? (
              <Select
                value={formData.azureApiKeyType}
                onValueChange={value => onInputChange('azureApiKeyType', value)}
                options={[
                  { value: '', label: t('aiProviders.fields.azureKeyTypePlaceholder', 'Select type') },
                  ...AZURE_KEY_TYPE_OPTIONS
                ]}
              />
            ) : (
              <Select
                value={formData.azureApiKeyType}
                onValueChange={value => onInputChange('azureApiKeyType', value)}
                options={[
                  { value: '', label: t('aiProviders.fields.googleKeyTypePlaceholder', 'Select type') },
                  ...GOOGLE_KEY_TYPE_OPTIONS
                ]}
              />
            )}
          </>
        ) : (
          <>
            <div />
            <div />
          </>
        )}

        {/* Fila 3: Base URL y API Key */}
        <TooltipLabel
          label={t('aiProviders.fields.baseUrl.label')}
          tooltip={t('aiProviders.fields.baseUrl.tooltip')}
        />
        <input
          type="url"
          value={formData.baseUrl}
          onChange={event => onInputChange('baseUrl', event.target.value)}
          placeholder={t('aiProviders.fields.baseUrl.placeholder')}
          className={inputClasses(Boolean(errors.baseUrl))}
        />

        <TooltipLabel
          label={t('aiProviders.fields.apiKey.label')}
          tooltip={t('aiProviders.fields.apiKey.tooltip')}
          required
        />
        <input
          type="password"
          value={formData.apiKey}
          onChange={event => onInputChange('apiKey', event.target.value)}
          placeholder={t('aiProviders.fields.apiKey.placeholder')}
          className={inputClasses(Boolean(errors.apiKey))}
        />

        {/* Fila 4: Enabled */}
        <TooltipLabel
          label={t('aiProviders.fields.status.label')}
          tooltip={t('aiProviders.fields.status.tooltip')}
        />
        <div className="flex h-10 items-center">
          <Toggle value={formData.status} onChange={value => onInputChange('status', value)} />
        </div>
        <div />
        <div />
      </div>

      {errorMessages}

      <div className="mt-6 flex flex-wrap gap-2 border-t border-border pt-6">
        {isEditMode && ['openai', 'anthropic', 'google'].includes(formData.provider) && (
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            disabled={refreshingCapabilities || !formData.apiKey}
            onClick={onRefreshCapabilities}
          >
            {refreshingCapabilities ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t('aiProviders.actions.refreshing')}
              </>
            ) : (
              <>
                <Bot className="h-4 w-4" />
                {t('aiProviders.actions.refreshCapabilities', 'Refresh Capabilities')}
              </>
            )}
          </Button>
        )}

        {isEditMode && formData.provider === 'azure' && (
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            disabled={refreshingVoices || !formData.apiKey}
            onClick={onRefreshVoices}
          >
            {refreshingVoices ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t('aiProviders.actions.refreshing')}
              </>
            ) : (
              <>
                <Mic className="h-4 w-4" />
                {t('aiProviders.actions.refreshVoices', 'Refresh Voices')}
              </>
            )}
          </Button>
        )}

        <Button
          type="button"
          variant="outline"
          className="gap-2"
          disabled={testingConnection || !formData.apiKey}
          onClick={onTestConnection}
        >
          {testingConnection ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              {t('aiProviders.actions.testing')}
            </>
          ) : (
            <>
              <TestTube className="h-4 w-4" />
              {t('aiProviders.actions.testConnection')}
            </>
          )}
        </Button>
      </div>
    </>
  );
}

